import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { apiClient } from "~/api/client"
import SlackMarkdown from "~/components/SlackMarkdown"
import AuthenticatedLayout from "~/components/AuthenticatedLayout"
import type { EntryDetail as EntryDetailType, Message } from "~/types"

const EntryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [entry, setEntry] = useState<EntryDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState("")
  const [notes, setNotes] = useState("")

  useEffect(() => {
    loadEntry()
  }, [id])

  const loadEntry = async () => {
    if (!id) {
      return
    }
    setLoading(true)
    try {
      const data = await apiClient.getEntry(id)
      setEntry(data)
      setTitleValue(data.title || "")
      setNotes(data.notes || "")
    } catch {
      // Failed to load entry
    } finally {
      setLoading(false)
    }
  }

  const handleTitleSave = async () => {
    if (!entry) {
      return
    }
    try {
      const updated = await apiClient.updateEntry(entry.id, {
        title: titleValue,
      })
      setEntry(updated)
      setEditingTitle(false)
    } catch {
      // Failed to update title
    }
  }

  const handleStatusToggle = async () => {
    if (!entry) {
      return
    }
    const newStatus = entry.status === "open" ? "resolved" : "open"
    try {
      const updated = await apiClient.updateEntry(entry.id, {
        status: newStatus,
      })
      setEntry(updated)
    } catch {
      // Failed to update status
    }
  }

  const handleNotesSave = async () => {
    if (!entry) {
      return
    }
    try {
      const updated = await apiClient.updateEntry(entry.id, { notes })
      setEntry(updated)
    } catch {
      // Failed to update notes
    }
  }

  const handleToggleSolution = async (message: Message) => {
    if (!entry) {
      return
    }
    try {
      await apiClient.toggleSolution(entry.id, message.id)
      await loadEntry()
    } catch {
      // Failed to toggle solution
    }
  }

  const handleToggleEmbedding = async (message: Message) => {
    if (!entry) {
      return
    }
    try {
      await apiClient.toggleEmbedding(entry.id, message.id)
      await loadEntry()
    } catch {
      // Failed to toggle embedding
    }
  }

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  }

  const isImageFile = (contentType: string) => {
    return contentType.startsWith("image/")
  }

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div style={{ textAlign: "center", padding: "48px" }}>Loading...</div>
      </AuthenticatedLayout>
    )
  }

  if (!entry) {
    return (
      <AuthenticatedLayout>
        <div style={{ textAlign: "center", padding: "48px" }}>
          Entry not found
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            marginBottom: "24px",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            {editingTitle ? (
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <input
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                  autoFocus
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    fontSize: "24px",
                    fontWeight: 600,
                    border: "1px solid #007bff",
                    borderRadius: "8px",
                  }}
                />
              </div>
            ) : (
              <div
                onClick={() => setEditingTitle(true)}
                style={{
                  margin: 0,
                  cursor: "pointer",
                  fontSize: "24px",
                  fontWeight: 600,
                }}
              >
                <SlackMarkdown inline>
                  {entry.title || "Untitled"}
                </SlackMarkdown>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
            <button
              type="button"
              onClick={handleStatusToggle}
              style={{
                padding: "8px 16px",
                backgroundColor:
                  entry.status === "open" ? "#28a745" : "#ffc107",
                color: entry.status === "open" ? "white" : "#000",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              Mark as {entry.status === "open" ? "Resolved" : "Open"}
            </button>
          </div>

          <div>
            <label
              style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}
            >
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <button
              type="button"
              onClick={handleNotesSave}
              style={{
                marginTop: "8px",
                padding: "8px 16px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              Save Notes
            </button>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #ccc",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "24px" }}>Messages</h3>

          {entry.messages.map((message) => (
            <div
              key={message.id}
              style={{
                marginBottom: "24px",
                paddingBottom: "24px",
                paddingTop: message.exclude_from_embedding ? "12px" : "0",
                paddingRight: message.exclude_from_embedding ? "12px" : "0",
                borderBottom: "1px solid #eee",
                paddingLeft: message.is_solution
                  ? "16px"
                  : message.exclude_from_embedding
                    ? "12px"
                    : "0",
                borderLeft: message.is_solution ? "4px solid #28a745" : "none",
                backgroundColor: message.exclude_from_embedding
                  ? "#f8f8f8"
                  : "transparent",
                borderRadius: message.exclude_from_embedding ? "8px" : "0",
                opacity: message.exclude_from_embedding ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: "8px",
                }}
              >
                <div>
                  <span style={{ fontWeight: 600 }}>
                    {message.user_display_name}
                  </span>
                  {message.is_parent && (
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "12px",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      Original message
                    </span>
                  )}
                  {message.is_solution && (
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "12px",
                        color: "#28a745",
                        fontWeight: 600,
                      }}
                    >
                      ✓ Solution
                    </span>
                  )}
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    {formatTimestamp(message.created_at)}
                  </span>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={() => handleToggleSolution(message)}
                    style={{
                      padding: "4px 12px",
                      backgroundColor: message.is_solution
                        ? "#ffc107"
                        : "#28a745",
                      color: message.is_solution ? "#000" : "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {message.is_solution ? "Unmark" : "Mark as Solution"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggleEmbedding(message)}
                    style={{
                      padding: "4px 12px",
                      backgroundColor: message.exclude_from_embedding
                        ? "#dc3545"
                        : "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                    title={
                      message.exclude_from_embedding
                        ? "Include in embeddings"
                        : "Exclude from embeddings"
                    }
                  >
                    {message.exclude_from_embedding ? "Excluded" : "Exclude"}
                  </button>
                </div>
              </div>

              <div
                style={{
                  marginBottom: message.attachments.length > 0 ? "12px" : "0",
                }}
              >
                <SlackMarkdown>{message.body}</SlackMarkdown>
              </div>

              {message.attachments.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {message.attachments.map((attachment) => (
                    <div key={attachment.id}>
                      {isImageFile(attachment.content_type) ? (
                        <img
                          src={attachment.storage_url}
                          alt={attachment.filename}
                          style={{
                            maxWidth: "400px",
                            maxHeight: "300px",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                          }}
                        />
                      ) : (
                        <a
                          href={attachment.storage_url}
                          download={attachment.filename}
                          style={{
                            display: "inline-block",
                            padding: "8px 12px",
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            textDecoration: "none",
                            color: "#007bff",
                            fontSize: "14px",
                          }}
                        >
                          📎 {attachment.filename}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default EntryDetail
